"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { pwaEventPath } from "@vereinsheim/backend-types/src/util/backendUrlHelper";
import { getDateWithDay, getTime } from "@/helpers/dateHelper";
import { EventWrapper } from "@/app/pwa/(main-layout)/events/lib/types";
import { isGameEventWrapper, isRegularEventWrapper } from "@/app/pwa/(main-layout)/events/lib/helper";
import { pwaTeamEventsPath, pwaTeamGamePath } from "@/utils/urlHelper";
import { Match, MatchHeader } from "@/app/pwa/components/match/Match";
import Info from "@/assets/icon/info.svg";
import { useTeams } from "@/app/pwa/lib/pwa-context";
import TeamTag from "../../team/TeamTag";

interface EventCardTitleInfoProps {
  eventWrapper: EventWrapper;
  isCanceled?: boolean;
  showTeamNames?: boolean
}

export function EventCardTitleInfo({ eventWrapper, isCanceled, showTeamNames }: EventCardTitleInfoProps) {
  const href = isRegularEventWrapper(eventWrapper) ? pwaEventPath(eventWrapper.event._id) : pwaTeamGamePath(eventWrapper.teamId, eventWrapper.matchId, {tab: eventWrapper.match?.State === "pre" ? "organisation" : "spieldaten"});

  const rawTeams = useTeams();
  const teamIds = useMemo(() => {
    return isRegularEventWrapper(eventWrapper) ? (eventWrapper.event.teamIds || []) : [eventWrapper.teamId]
  }, [eventWrapper])
  const eventTeams = rawTeams.filter(t => teamIds?.includes(t.Id?.toString() ?? ""))

  const canceled = isCanceled ? <div className="text-warning font-semibold flex items-center gap-0.5 w-full py-1 mt-2 border-t border-b border-warning justify-center"><Info className="w-4 h-4" />Abgesagt</div> : null

  return (
    <div>
      {isGameEventWrapper(eventWrapper) &&
        <>
          <div className="px-3 pt-3">
            {showTeamNames && eventTeams.length >= 1 &&
              <div className="flex items-baseline gap-1 mb-2">
                {eventTeams.map(team =>
                  team.Id &&
                    <TeamTag key={team.Id} href={pwaTeamEventsPath(team.Id?.toString())} team={team} />
                )}
              </div>
            }
          </div>
          <Link href={href} className={"flex flex-col duration-fast hover:bg-gray-100/50 active:bg-gray-100"}>
            <MatchHeader match={eventWrapper.match}/>
            <Match
              className="py-4"
              key={`ClubTeamSchedule-${eventWrapper.teamId}-${eventWrapper.match?.Id}`}
              match={eventWrapper.match}
              teamId={parseInt(eventWrapper.teamId!)}
              widget={true} // to remove all links and prevent hydration errors
            />
            {canceled}
          </Link>
        </>
        ||
        <>
          <div className="px-3 pt-3">
            {showTeamNames && eventTeams.length >= 1 &&
              <div className="flex items-baseline gap-1 mb-2">
                {eventTeams.map(team =>
                  team.Id &&
                  <TeamTag key={team.Id} href={pwaTeamEventsPath(team.Id?.toString())} team={team} />
                )}
              </div>
            }
          </div>

          <Link href={href}
                className="flex flex-col justify-center items-start px-3 pb-3 duration-fast hover:bg-gray-100/50 active:bg-gray-100">
            <h3 className={`text-lg font-semibold ${isCanceled ? "line-through decoration-2" : ""}`}>
              {isRegularEventWrapper(eventWrapper ) ? eventWrapper.event.title : "Event"}
            </h3>
            <div className="flex items-center justify-center gap-2">
              <span className="">{getDateWithDay(eventWrapper.start / 1000)}</span>
              <span className="w-0.5 bg-gray-150 h-4"></span>
              <span className="">{getTime(eventWrapper.start)} Uhr</span>
            </div>
            {canceled}
          </Link>
        </>}

    </div>
  );
}
